import axios from 'axios';
import { createLogger } from '../utils/logger';

// Create a logger instance for API service
const logger = createLogger('APIService');

// API URL Strategy based on environment and runtime context:
//
// IMPORTANT: There are two key environments to consider:
// 1. The code running in the browser: Always needs to use either an external URL or proxy
// 2. The code running in node.js (SSR): Can use container names like "rag-api"
//
// For browser access:
// - In development: Use proxy through empty baseURL
// - In production: Use explicit public API URL
// 
// For server-to-server calls (not relevant for this app currently):
// - Use container names like "http://rag-api:8000"
//
const isDocker = process.env.REACT_APP_IS_DOCKER === 'true';
// For browser code, we must use empty baseURL in development to use the proxy
// In production, we must use a real URL the browser can access
// In development we still use a relative path, but include the public '/api' prefix
// so requests from localhost:3000 are routed to /api/* (and proxied to the backend).
const baseURL = process.env.NODE_ENV === 'development'
  ? '/api'
  : (process.env.REACT_APP_API_URL ?? '/api');

// Enhanced logging for API configuration
logger.info('API SERVICE INITIALIZATION');
logger.info(`Node environment: ${process.env.NODE_ENV}`);
logger.info(`API endpoint from env: ${process.env.REACT_APP_API_URL || '(empty, using proxy)'}`);
logger.info(`Using base URL: ${baseURL || '(empty, using proxy)'}`);

// Immediately try a test request to check connectivity
setTimeout(() => {
  logger.info('Initiating automatic health check...');
  
  // Browser always needs to use either a proxy or a public URL it can access
  // In development, always use the proxy via relative URL
  const healthCheckUrl = process.env.NODE_ENV === 'development'
    ? '/api/health'  // Use /api prefix in dev so proxy/ingress maps correctly
    : `${baseURL}/health`;  // Uses explicit URL in production
  
  logger.info(`Health check URL: ${healthCheckUrl}`);
  logger.info(`Node environment: ${process.env.NODE_ENV}`);
  logger.info(`Is Docker: ${isDocker}`);
  logger.info(`Base URL: ${baseURL || '(empty, using proxy)'}`);
  
  // IMPORTANT BROWSER NETWORKING CONTEXT:
  logger.info('BROWSER NETWORK INFO:');
  logger.info(`Origin: ${window.location.origin}`);
  logger.info(`Host: ${window.location.host}`);
  
  fetch(healthCheckUrl)
    .then(response => {
      logger.info(`Health check response status: ${response.status}`);
      return response.json();
    })
    .then(data => {
      logger.info('Health check response data:', data);
    })
    .catch(error => {
      logger.error('Health check fetch error:', error);
      logger.error(`Current browser location: ${window.location.origin}`);
      logger.error(`Current environment: ${process.env.NODE_ENV}`);
      logger.error('Check that the API is running and accessible from the browser');
    });
}, 2000);

// Create axios instance
const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor
api.interceptors.request.use(
  (config) => {
    // Log request details
    logger.info(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add authentication token if available
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.email) {
        config.headers['X-User-Email'] = userData.email;
        logger.debug(`User email added to request headers: ${userData.email}`);
      }
    }
    
    // Log the full request URL being used
    const fullUrl = `${config.baseURL || ''}${config.url}`;
    logger.debug(`Full request URL: ${fullUrl}`);
    
    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor
api.interceptors.response.use(
  (response) => {
    // Log successful responses
    logger.info(`API Response Success: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    logger.debug(`Response status: ${response.status}`);
    return response;
  },
  (error) => {
    // Detailed error logging
    logger.error('API Response Error:', {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText
    });
    
    // Log detailed network error information if available
    if (error.code === 'ECONNABORTED') {
      logger.error('Request timeout occurred');
    } else if (error.message === 'Network Error') {
      logger.error('Network error - API server may be unreachable');
      logger.error(`Current baseURL: ${baseURL}`);
      logger.error(`Check if API is running at: ${baseURL || 'proxy URL in package.json'}`);
    }
    
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      logger.warn('Authentication error (401) - User will be logged out');
      // Only clear local storage if user data exists
      // Do NOT redirect here - let React components handle redirects
      const user = localStorage.getItem('user');
      if (user) {
        localStorage.removeItem('user');
        logger.info('User data removed from localStorage due to auth error');
        // Don't use window.location.href - let React Router handle navigation
      }
    }
    
    // Log the complete error object in development mode
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Complete error details:', error);
    }
    
    return Promise.reject(error);
  }
);

// Add a health check function to verify API connectivity
export const checkApiHealth = async () => {
  try {
    logger.info('Performing API health check...');
    
    // Use axios instance which is already configured with the correct baseURL
    const response = await api.get('/health');
    logger.info('API health check successful:', response.data);
    return {
      success: true,
      data: response.data,
      message: 'API is reachable and healthy'
    };
  } catch (error) {
    logger.error('API health check failed:', error);
    
    // Log additional information for debugging
    logger.error(`API base URL: ${api.defaults.baseURL || '(empty, using proxy)'}`);
    logger.error(`Environment: ${process.env.NODE_ENV}`);
    
    return {
      success: false,
      error,
      message: 'Unable to reach API server. Please check server status and network connectivity.'
    };
  }
};

export default api;
